import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

const inputClass =
  "mt-1 w-full rounded-lg border border-line px-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";

function LoginPage() {
  const navigate = useNavigate();
  const { applyToken } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await api.login(email, password);
      await applyToken(res.token);
      navigate({ to: "/" });
    } catch (err) {
      setError((err as Error).message || "Innskráning mistókst.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="grid min-h-screen place-items-center bg-gradient-to-br from-[#0B1120] via-[#0D1B33] to-[#122b52] px-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <img
            src="/logo/atomika-mark-512px.png"
            alt="Atomika"
            className="mb-4 h-14 w-14"
          />
          <h1 className="text-2xl font-bold text-white">Velkomin aftur</h1>
          <p className="mt-1 text-sm text-white/50">
            Skráðu þig inn til að opna vinnusvæðið þitt.
          </p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/95 p-6 shadow-[0_24px_80px_rgba(0,0,0,.4)] backdrop-blur">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink">
                Netfang
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-ink">
                Lykilorð
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className={`${inputClass} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Fela lykilorð" : "Sýna lykilorð"}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 mt-1 flex items-center px-3 text-muted-3 hover:text-ink"
                >
                  {showPassword ? (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07A3 3 0 1 1 9.9 9.9" />
                      <path d="M6.61 6.61A18.5 18.5 0 0 0 2 12s3 8 10 8a9.12 9.12 0 0 0 5.39-1.61" />
                      <path d="m2 2 20 20" />
                    </svg>
                  ) : (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8Z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-brand px-4 py-2.5 font-semibold text-white transition hover:bg-brand-dark disabled:opacity-50"
            >
              {loading ? "Skrái inn…" : "Skrá inn"}
            </button>
          </form>
        </div>

        <div className="mt-5 text-center text-[13px] text-white/40">
          <Link
            to="/gleymt-lykilord"
            className="text-white/70 underline underline-offset-2 hover:text-white"
          >
            Gleymt lykilorð?
          </Link>
          <div className="mt-3">
            <Link to="/velkomin" className="text-white/40 hover:text-white/70">
              ← Til baka
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
